/**
 * Notification hooks — TanStack Query for inbox notifications.
 * Adaptive polling: chat=5s, inbox=30s, background=60s, hidden=pause.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchCombinedUnreadCount,
} from '@turnifly/shared/api'
import type { NotificationItem, CombinedUnread } from '@turnifly/shared/api'

// ── Query Keys ──────────────────────────────────────────────

export const inboxKeys = {
  all: ['inbox'] as const,
  notifications: (userId: string, type?: string) =>
    ['inbox', 'notifications', userId, type] as const,
  combinedUnread: (userId: string) =>
    ['inbox', 'unread', userId] as const,
} as const

// ── Adaptive polling interval ───────────────────────────────

export function useAdaptiveInterval(activeMs: number): number | false {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const handleVisibility = (): void => {
      setIsVisible(document.visibilityState === 'visible')
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  if (!isVisible) return false
  return activeMs
}

// ── Notifications list ──────────────────────────────────────

export function useNotifications(
  userId: string | undefined,
  type?: string,
): ReturnType<typeof useQuery<NotificationItem[]>> {
  const interval = useAdaptiveInterval(30_000)

  return useQuery({
    queryKey: inboxKeys.notifications(userId ?? '', type),
    queryFn: () => fetchNotifications(userId!, type),
    enabled: !!userId,
    staleTime: 30_000,
    refetchInterval: interval,
  })
}

// ── Mark single notification read ───────────────────────────

export function useMarkNotificationRead(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboxKeys.all })
    },
  })
}

// ── Mark all notifications read ─────────────────────────────

export function useMarkAllRead(
  userId: string | undefined,
): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      if (!userId) throw new Error('No user id')
      return markAllNotificationsRead(userId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboxKeys.all })
    },
  })
}

// ── Combined unread count (bell badge) ──────────────────────

export function useCombinedUnreadCount(
  userId: string | undefined,
  pollingMs = 60_000,
): ReturnType<typeof useQuery<CombinedUnread>> {
  const interval = useAdaptiveInterval(pollingMs)

  return useQuery({
    queryKey: inboxKeys.combinedUnread(userId ?? ''),
    queryFn: () => fetchCombinedUnreadCount(userId!),
    enabled: !!userId,
    staleTime: 30_000,
    refetchInterval: interval,
  })
}

// ── Helper: invalidate all inbox queries ────────────────────

export function useInvalidateInbox(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: inboxKeys.all })
  }, [queryClient])
}
